// Lecture d'un log de démarrage de vLLM, de SGLang ou de llama.cpp (llama-server, Ollama, LM Studio) :
// la mémoire réellement prise par les poids, le cache KV et les tampons de calcul. Sert à la mesure
// sur GPU loué (scripts/measure-gpu.ts) et aux journaux d'InferenceX (scripts/sources/inferencex.ts) ;
// le calculateur n'en garde que compareGiB (volet « Compare with your real usage » retiré en octobre 2026).
// Formats relevés sur vLLM 0.10 à 0.21, SGLang 0.5 et llama.cpp b6000+ ; les anciennes formulations
// sont gardées tant qu'elles circulent.

export type LogReading = {
  engine: "vllm" | "sglang" | "llamacpp";
  /** Poids sur un GPU (vLLM et SGLang, par rang tensoriel) ou au total, GPU et RAM confondus (llama.cpp). */
  weightsGiB?: number;
  /** llama.cpp : part des poids restée en RAM (CPU, CPU_Mapped). */
  weightsCpuGiB?: number;
  /** vLLM et SGLang : réserve de cache KV d'un GPU ; llama.cpp : cache KV alloué, au total. */
  kvGiB?: number;
  /** vLLM et SGLang : tokens que la réserve contient. */
  kvTokens?: number;
  /** vLLM : gpu_memory_utilization ; SGLang : mem_fraction_static. */
  utilization?: number;
  /** llama.cpp : octets par élément du cache (f16 : 2, q8_0 : 8,5/8, q4_0 : 4,5/8). */
  kvBytes?: number;
  /** llama.cpp : tampons de calcul ; vLLM et SGLang : graphes CUDA. */
  computeGiB?: number;
  context?: number;
  sequences?: number;
  tp?: number;
  /** Format des poids reconnu (identifiant du calculateur), et tel qu'écrit dans le log. */
  format?: string;
  formatLabel?: string;
  layers?: { gpu: number; total: number };
  model?: string;
};

const num = (s: string) => Number(s.replace(/,/g, ""));
const first = (text: string, ...res: RegExp[]) => {
  for (const re of res) {
    const m = text.match(re);
    if (m) return m;
  }
  return null;
};
const MiB = 1 / 1024;

/** « Q4_K - Medium » (llama.cpp) → q4_k_m, l'identifiant du calculateur. */
const LLAMA_FILE_TYPES: [RegExp, string][] = [
  [/^Q8_0\b/, "q8_0"], [/^Q6_K\b/, "q6_k"], [/^Q5_K - Medium/, "q5_k_m"], [/^Q4_K - Medium/, "q4_k_m"],
  [/^Q3_K - Medium/, "q3_k_m"], [/^BF16\b/, "bf16"], [/^F16\b/, "bf16"], [/^MXFP4/, "mxfp4"],
];

function sumBuffers(text: string, re: RegExp) {
  let gpu = 0, cpu = 0, found = false;
  for (const m of text.matchAll(re)) {
    found = true;
    const mib = num(m[2]);
    // CPU, CPU_Mapped, CPU_REPACK et les tampons « _Host » (mémoire de l'hôte épinglée) sont en RAM.
    if (/^CPU|_Host$/i.test(m[1])) cpu += mib; else gpu += mib;
  }
  return found ? { gpu: gpu * MiB, cpu: cpu * MiB } : undefined;
}

export function parseServingLog(text: string): LogReading | null {
  const isSglang = /Load weight end\.|KV Cache is allocated|max_total_num_tokens=/.test(text);
  const isVllm = /vllm|Model loading took|KV cache size: [\d,]+ tokens/i.test(text);
  const isLlama = /load_tensors|llama_context|llama_kv_cache|llama_model_loader|print_info:/.test(text);
  if (isLlama) {
    const model = sumBuffers(text, /(?:load_tensors|llm_load_tensors):\s+(\S+) model buffer size\s*=\s*([\d.]+) MiB/g);
    const kv = sumBuffers(text, /(?:llama_kv_cache\w*|llama_kv_cache_init):\s+(\S+) KV buffer size\s*=\s*([\d.]+) MiB/g);
    const compute = sumBuffers(text, /(?:llama_context|llama_new_context_with_model):\s+(\S+) compute buffer size\s*=\s*([\d.]+) MiB/g);
    const ctx = first(text, /\bn_ctx\s+=\s+(\d+)/);
    const seqs = first(text, /\bn_seq_max\s+=\s+(\d+)/);
    const ftype = first(text, /file type\s+=\s+([^\n(]+)/)?.[1]?.trim();
    const layers = first(text, /offloaded (\d+)\/(\d+) layers to GPU/);
    const name = first(text, /general\.name\s+str\s+=\s+([^\n]+)/)?.[1]?.trim();
    const kvType = first(text, /K \((\w+)\):/)?.[1]?.toLowerCase();
    const kvBytes = kvType ? ({ f16: 2, bf16: 2, f32: 4, q8_0: 8.5 / 8, q4_0: 4.5 / 8, q4_1: 5 / 8, q5_0: 5.5 / 8, q5_1: 6 / 8 } as Record<string, number>)[kvType] : undefined;
    if (!model && !kv) return null;
    return {
      engine: "llamacpp",
      ...(model ? { weightsGiB: model.gpu + model.cpu, weightsCpuGiB: model.cpu } : {}),
      ...(kv ? { kvGiB: kv.gpu + kv.cpu } : {}),
      ...(compute ? { computeGiB: compute.gpu + compute.cpu } : {}),
      ...(ctx ? { context: num(ctx[1]) } : {}),
      ...(seqs ? { sequences: num(seqs[1]) } : {}),
      ...(kvBytes ? { kvBytes } : {}),
      ...(ftype ? { formatLabel: ftype, format: LLAMA_FILE_TYPES.find(([re]) => re.test(ftype))?.[1] } : {}),
      ...(layers ? { layers: { gpu: num(layers[1]), total: num(layers[2]) } } : {}),
      ...(name ? { model: name } : {}),
    };
  }
  if (isSglang) {
    // « Load weight end. … mem usage=79.80 GB », « KV Cache is allocated. #tokens: 509285, KV size:
    // 33.33 GB » (avant 0.5 : « K size: …, V size: … »). Ses « GB » sont des Gio : octets ÷ 2^30
    // (get_available_gpu_memory, sglang/srt/utils).
    const weights = first(text, /Load weight end\.[^\n]*?mem usage=([\d.]+) ?GB/);
    const kv = text.match(/KV Cache is allocated\.\s*#tokens:\s*([\d,]+),\s*(?:KV size:\s*([\d.]+) ?GB|K size:\s*([\d.]+) ?GB,\s*V size:\s*([\d.]+) ?GB)/);
    const graphs = first(text, /Capture cuda graph end\.[^\n]*?mem usage=([\d.]+) ?GB/);
    const ctx = first(text, /\bcontext_len=(\d+)/);
    const seqs = first(text, /\bmax_running_requests=(\d+)/);
    const tp = first(text, /\btp_size=(\d+)/);
    const util = first(text, /\bmem_fraction_static=([\d.]+)/);
    const name = first(text, /\bmodel_path='([^']+)'/);
    if (!weights && !kv) return null;
    const kvGiB = kv ? (kv[2] ? num(kv[2]) : num(kv[3]) + num(kv[4])) : undefined;
    return {
      engine: "sglang",
      ...(weights ? { weightsGiB: num(weights[1]) } : {}),
      ...(kvGiB !== undefined ? { kvGiB, kvTokens: num(kv![1]) } : {}),
      ...(graphs ? { computeGiB: num(graphs[1]) } : {}),
      ...(ctx ? { context: num(ctx[1]) } : {}),
      ...(seqs ? { sequences: num(seqs[1]) } : {}),
      ...(tp ? { tp: num(tp[1]) } : {}),
      ...(util ? { utilization: num(util[1]) } : {}),
      ...(name ? { model: name[1] } : {}),
    };
  }
  if (isVllm) {
    const weights = first(text, /Model loading took ([\d.]+) ?GiB/i, /Loading model weights took ([\d.]+) ?GB/i, /model weights take ([\d.]+) ?GiB/i);
    const kv = first(text, /Available KV cache memory: ([\d.]+) ?GiB/i, /memory reserved for KV Cache is ([\d.]+) ?GiB/i);
    const tokens = first(text, /GPU KV cache size: ([\d,]+) tokens/i);
    const graphs = first(text, /Graph capturing finished in [\d.]+ secs?, took ([\d.]+) GiB/i);
    // Arguments écrits « clé=valeur » (jusqu'à 0.12) ou en dictionnaire (« 'max_model_len': 10240 »).
    const ctx = first(text, /max_seq_len=(\d+)/, /max_model_len[=':\s]+(\d+)/, /Maximum concurrency for ([\d,]+) tokens per request/i);
    const seqs = first(text, /max_num_seqs[=':\s]+(\d+)/);
    const tp = first(text, /tensor_parallel_size[=':\s]+(\d+)/);
    const util = first(text, /gpu_memory_utilization[=':\s]+([\d.]+)/);
    const name = first(text, /\bmodel='([^']+)'/, /'model': '([^']+)'/);
    if (!weights && !kv) return null;
    return {
      engine: "vllm",
      ...(weights ? { weightsGiB: num(weights[1]) } : {}),
      ...(kv ? { kvGiB: num(kv[1]) } : {}),
      ...(tokens ? { kvTokens: num(tokens[1]) } : {}),
      ...(graphs ? { computeGiB: num(graphs[1]) } : {}),
      ...(ctx ? { context: num(ctx[1]) } : {}),
      ...(seqs ? { sequences: num(seqs[1]) } : {}),
      ...(tp ? { tp: num(tp[1]) } : {}),
      ...(util ? { utilization: num(util[1]) } : {}),
      ...(name ? { model: name[1] } : {}),
    };
  }
  return null;
}

/** Écart relatif entre une mesure et notre estimation, et son verdict. */
export function compareGiB(measured: number, ours: number): { delta: number; verdict: "match" | "close" | "differs" } {
  const delta = ours > 0 ? (ours - measured) / measured : Infinity;
  const a = Math.abs(delta);
  return { delta, verdict: a <= 0.05 ? "match" : a <= 0.15 ? "close" : "differs" };
}
